import { createSlice } from '@reduxjs/toolkit'

// Initial state
const initialState = {
  searchFilters: {
    specialty: 'General Practitioner',
    address: '',
    consultType: [],
    timeSlot: '',
    languages: [],
    gender: '',
    medicalAids: [],
    minRating: '',
    searchType: 'general_practitioner',
    placeId: '',
    location: null,
    maxDistance: 25
  },
  searchResults: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 355,
    totalResults: 7081,
    resultsPerPage: 20
  },
  searchHistory: []
}

// Create slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action) => {
      state.loading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },

    // Search filters
    setSearchFilters: (state, action) => {
      state.searchFilters = { ...state.searchFilters, ...action.payload }
    },

    updateFilter: (state, action) => {
      const { filterType, value } = action.payload

      // Handle array filters (checkboxes)
      if (['consultType', 'languages', 'medicalAids'].includes(filterType)) {
        if (!state.searchFilters[filterType]) {
          state.searchFilters[filterType] = []
        }

        if (Array.isArray(value)) {
          state.searchFilters[filterType] = value
        } else {
          const index = state.searchFilters[filterType].indexOf(value)
          if (index > -1) {
            state.searchFilters[filterType] = state.searchFilters[filterType].filter((item) => item !== value)
          } else {
            state.searchFilters[filterType] = [...state.searchFilters[filterType], value]
          }
        }
      } else {
        // Handle all other filters including the new ones
        state.searchFilters[filterType] = value
      }
    },

    resetSearchFilters: (state) => {
      state.searchFilters = {
        specialty: 'General Practitioner',
        address: '',
        consultType: [],
        timeSlot: '',
        languages: [],
        gender: '',
        medicalAids: [],
        minRating: '',
        searchType: 'general_practitioner',
        placeId: '',
        location: null,
        maxDistance: 25
      }
    },

    // Search results
    setSearchResults: (state, action) => {
      state.searchResults = action.payload
    },

    clearSearchResults: (state) => {
      state.searchResults = []
    },

    // Pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },

    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload
    },

    // Search history
    addToSearchHistory: (state, action) => {
      const newSearch = {
        filters: action.payload.filters,
        timestamp: new Date().toISOString(),
        resultsCount: action.payload.resultsCount || 0
      }

      // Remove duplicate searches
      state.searchHistory = state.searchHistory.filter(
        (search) => JSON.stringify(search.filters) !== JSON.stringify(newSearch.filters)
      )

      // Add new search to beginning and limit to 10
      state.searchHistory = [newSearch, ...state.searchHistory].slice(0, 10)
    },

    clearSearchHistory: (state) => {
      state.searchHistory = []
    },

    // Complex actions (these will be handled by async thunks)
    performSearchStart: (state) => {
      state.loading = true
      state.error = null
    },

    performSearchSuccess: (state, action) => {
      state.loading = false
      state.searchResults = action.payload.results || action.payload

      // Update pagination if provided
      if (action.payload.pagination) {
        state.pagination = { ...state.pagination, ...action.payload.pagination }
      } else if (Array.isArray(action.payload)) {
        state.pagination = {
          ...state.pagination,
          currentPage: 1,
          totalResults: action.payload.length
        }
      }
    },

    performSearchFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
      state.searchResults = []
    },

    changePageStart: (state) => {
      state.loading = true
      state.error = null
    },

    changePageSuccess: (state, action) => {
      state.loading = false
      state.searchResults = action.payload.results || action.payload

      if (action.payload.pagination) {
        state.pagination = { ...state.pagination, ...action.payload.pagination }
      } else {
        state.pagination = { ...state.pagination, currentPage: action.payload.page }
      }
    },

    changePageFailure: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    // Reset entire state
    resetSearch: (state) => {
      return { ...initialState, searchHistory: state.searchHistory }
    }
  }
})

// Export actions
export const {
  setLoading,
  setError,
  clearError,
  setSearchFilters,
  updateFilter,
  resetSearchFilters,
  setSearchResults,
  clearSearchResults,
  setPagination,
  setCurrentPage,
  addToSearchHistory,
  clearSearchHistory,
  performSearchStart,
  performSearchSuccess,
  performSearchFailure,
  changePageStart,
  changePageSuccess,
  changePageFailure,
  resetSearch
} = searchSlice.actions

// Export reducer
export default searchSlice

// Selectors
export const selectSearchState = (state) => state.searchEngine
export const selectSearchFilters = (state) => state.searchEngine.searchFilters
export const selectSearchResults = (state) => state.searchEngine.searchResults
export const selectSearchLoading = (state) => state.searchEngine.loading
export const selectSearchError = (state) => state.searchEngine.error
export const selectSearchPagination = (state) => state.searchEngine.pagination
export const selectSearchHistory = (state) => state.searchEngine.searchHistory
